import React from 'react';
import { Shield, CheckCircle } from 'lucide-react';
import { Role, Permission } from '../../types';
import { Theme } from '../../ThemeContext';

interface RoleViewModeProps {
  role: Role;
  permissions: Permission[];
  theme: Theme;
}

const RoleViewMode: React.FC<RoleViewModeProps> = ({ role, permissions, theme }) => {
  const getPermissionDetails = (permissionIds: string[]) => {
    return permissionIds
      .map(id => permissions.find(p => p.permission_id === id))
      .filter(Boolean) as Permission[];
  };

  const rolePermissions = getPermissionDetails(role.permissions);

  // Group permissions by resource type
  const groupedPermissions = rolePermissions.reduce((acc, perm) => {
    if (!acc[perm.resource_type]) {
      acc[perm.resource_type] = [];
    }
    acc[perm.resource_type].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Basic Information */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0 }}>
          Basic Information
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Role Name
            </label>
            <div style={{ color: theme.text, fontSize: 16, textTransform: 'uppercase', fontWeight: 600 }}>
              {role.name}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Description
            </label>
            <div style={{ color: theme.text, fontSize: 14 }}>
              {role.description || 'No description provided'}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Type
            </label>
            <div>
              {role.is_system_role ? (
                <span style={{
                  padding: '4px 8px',
                  background: '#e0f2fe',
                  color: '#0369a1',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  System Role
                </span>
              ) : (
                <span style={{
                  padding: '4px 8px',
                  background: '#f0fdf4',
                  color: '#15803d',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  Custom Role
                </span>
              )}
            </div>
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Created At
            </label>
            <div style={{ color: theme.text, fontSize: 14 }}>
              {new Date(role.created_at).toLocaleString()}
            </div>
          </div>

          {role.updated_at && role.updated_at !== role.created_at && (
            <div>
              <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Updated At
              </label>
              <div style={{ color: theme.text, fontSize: 14 }}>
                {new Date(role.updated_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Permissions */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={20} />
          Permissions ({rolePermissions.length})
        </h2>

        {rolePermissions.length === 0 ? (
          <div style={{ color: theme.textSecondary, fontSize: 14 }}>
            No permissions assigned to this role
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {Object.entries(groupedPermissions).map(([resourceType, perms]) => (
              <div key={resourceType}>
                <h3 style={{ 
                  color: theme.text, 
                  fontSize: 16, 
                  marginBottom: 12, 
                  marginTop: 0,
                  textTransform: 'capitalize',
                  fontWeight: 600 
                }}>
                  {resourceType}
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {perms.map(perm => (
                    <div
                      key={perm.permission_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 12px',
                        background: theme.background,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 6,
                      }}
                    >
                      <CheckCircle size={14} color={theme.accent} />
                      <span style={{ color: theme.text, fontSize: 13, fontWeight: 500, textTransform: 'uppercase' }}>
                        {perm.action}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleViewMode;
